import type { DriverAction } from './actions';
import { DriverActionTypes } from './driverActionTypes';
import { handleOnSaveDriver } from './handlers/handleOnSaveDriver';
import { handleOnSaveTokenAssociation } from './handlers/handleOnSaveTokenAssociation';

import {
  type Associations,
  type Driver,
  type NewDriver,
  type TokenValidation,
  type WebData,
  mapWebData,
  webDataError,
  webDataFetching,
  webDataNotAsked,
  webDataSuccess,
} from '@cobliteam/api-lib';

export interface DriverState {
  driversWebData: WebData<Driver[]>;
  saveTokenAssociationWebData: WebData<void>;
  saveDriverWebData: WebData<Driver>;
  deleteDriverWebData: WebData<void>;
  tokenAssociationsWebData: WebData<Associations>;
  tokenValidationWebData: WebData<TokenValidation>;
  driverFormData: Driver | NewDriver;
  generateDriverAppCodeWebData: WebData<void>;
}

export const driversInitialState: DriverState = {
  driversWebData: webDataNotAsked(),
  saveTokenAssociationWebData: webDataNotAsked(),
  saveDriverWebData: webDataNotAsked(),
  tokenAssociationsWebData: webDataNotAsked(),
  tokenValidationWebData: webDataNotAsked(),
  deleteDriverWebData: webDataNotAsked(),
  driverFormData: {
    id: undefined,
    name: '',
    phoneNumbers: [''],
    identifier: '',
    driverCode: undefined,
    licenseCategory: undefined,
    firstLicenseAcquirementDateInMillis: undefined,
    licenseExpirationDateInMillis: undefined,
  },
  generateDriverAppCodeWebData: webDataNotAsked(),
};

export const driversReducer = (state: DriverState = driversInitialState, action: DriverAction): DriverState => {
  switch (action.type) {
    case DriverActionTypes.LOAD_DRIVERS_REQUEST:
      return {
        ...state,
        driversWebData: webDataFetching(),
      };

    case DriverActionTypes.LOAD_DRIVERS_SUCCEEDED:
      return {
        ...state,
        driversWebData: webDataSuccess(action.payload),
      };

    case DriverActionTypes.LOAD_DRIVERS_FAILED:
      return {
        ...state,
        driversWebData: webDataError(),
      };

    case DriverActionTypes.DELETE_DRIVER_REQUEST:
      return {
        ...state,
        deleteDriverWebData: webDataFetching(),
      };

    case DriverActionTypes.DELETE_DRIVER_SUCCEEDED:
      return {
        ...state,
        deleteDriverWebData: webDataSuccess(undefined),
        driversWebData: mapWebData((drivers: Driver[]) => drivers.filter((driver) => driver.id !== action.payload.driverId))(state.driversWebData),
      };

    case DriverActionTypes.DELETE_DRIVER_FAILED:
      return {
        ...state,
        deleteDriverWebData: webDataError(),
      };

    case DriverActionTypes.SAVE_TOKEN_ASSOCIATION_REQUEST:
      return {
        ...state,
        saveTokenAssociationWebData: webDataFetching(),
      };

    case DriverActionTypes.SAVE_DRIVER_REQUEST:
      return {
        ...state,
        saveDriverWebData: webDataFetching(),
      };

    case DriverActionTypes.SAVE_DRIVER_FAILED:
      return {
        ...state,
        saveDriverWebData: webDataError(),
      };

    case DriverActionTypes.SAVE_DRIVER_SUCCEEDED:
      return handleOnSaveDriver(state, action);

    case DriverActionTypes.SAVE_TOKEN_ASSOCIATION_SUCCEEDED:
      return handleOnSaveTokenAssociation(state, action);

    case DriverActionTypes.SAVE_TOKEN_ASSOCIATION_FAILED:
      return {
        ...state,
        saveTokenAssociationWebData: webDataError(),
      };

    case DriverActionTypes.LOAD_TOKEN_ASSOCIATIONS_REQUEST:
      return {
        ...state,
        tokenAssociationsWebData: webDataFetching(),
      };

    case DriverActionTypes.LOAD_TOKEN_ASSOCIATIONS_SUCCEEDED:
      return {
        ...state,
        tokenAssociationsWebData: webDataSuccess(action.payload),
      };

    case DriverActionTypes.LOAD_TOKEN_ASSOCIATIONS_FAILED:
      return {
        ...state,
        tokenAssociationsWebData: webDataError(),
      };

    case DriverActionTypes.TOKEN_VALIDATION_REQUEST:
      return {
        ...state,
        tokenValidationWebData: webDataFetching(),
      };

    case DriverActionTypes.TOKEN_VALIDATION_SUCCEEDED:
      return {
        ...state,
        tokenValidationWebData: webDataSuccess(action.payload),
      };

    case DriverActionTypes.TOKEN_VALIDATION_FAILED:
      return {
        ...state,
        tokenValidationWebData: webDataError(),
      };

    case DriverActionTypes.ON_DRIVER_DATA_FORM_CHANGE:
      return {
        ...state,
        driverFormData: {
          ...state.driverFormData,
          ...action.payload,
        },
      };

    default:
      return state;
  }
};
